/** Ordered description body: typed text lines interleaved with referenced audit chips. */

export type ReportTextBlock = {
  id: string,
  kind: 'text',
  text: string,
};

export type ReportLogBlock = {
  id: string,
  kind: 'log',
  sourceId: string,
  tmpId: number,
  message: string,
  type: string,
  date?: string,
  value?: string,
  units?: string,
  /** Frozen /audits Value-header when this chip was inserted. */
  valueMode?: 'value' | 'units',
  /** Merged frozen source ids when inserted from a combined SKU row. */
  combinedSourceIds?: string[],
  /** Combined N · unique audit types — combined preview/PDF only. */
  combinedDetails?: string,
  /** Actual log type / SKU when `type` is a department or inventory-group label. */
  auditType?: string,
};

export type ReportDescriptionBlock = ReportTextBlock | ReportLogBlock;

export const newReportBlockId = () => (
  `rb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
);

export const createEmptyTextBlock = (text = ''): ReportTextBlock => ({
  id: newReportBlockId(),
  kind: 'text',
  text,
});

export const createInitialDescriptionBlocks = (): ReportDescriptionBlock[] => (
  [createEmptyTextBlock()]
);

/** Typed text only — used for the description char budget. */
export const sumDescriptionTextChars = (blocks: ReportDescriptionBlock[]): number => (
  blocks.reduce((n, b) => (b.kind === 'text' ? n + b.text.length : n), 0)
);

export const plainDescriptionFromBlocks = (blocks: ReportDescriptionBlock[]): string => (
  blocks
    .filter((b): b is ReportTextBlock => b.kind === 'text')
    .map((b) => b.text)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
);

export const referencedSourceIdsFromBlocks = (
  blocks: ReportDescriptionBlock[],
): Set<string> => {
  const ids = new Set<string>();
  blocks.forEach((b) => {
    if (b.kind !== 'log') return;
    if (b.combinedSourceIds?.length) {
      b.combinedSourceIds.forEach((id) => ids.add(id));
      return;
    }
    ids.add(b.sourceId);
  });
  return ids;
};

export const logBlocksFromDescription = (
  blocks: ReportDescriptionBlock[],
): ReportLogBlock[] => blocks.filter((b): b is ReportLogBlock => b.kind === 'log');

/** Merge adjacent text blocks after a log is removed. */
export const normalizeDescriptionBlocks = (
  blocks: ReportDescriptionBlock[],
): ReportDescriptionBlock[] => {
  const next: ReportDescriptionBlock[] = [];
  blocks.forEach((block) => {
    const prev = next[next.length - 1];
    if (block.kind === 'text' && prev?.kind === 'text') {
      // Join with a newline so two paragraphs stay readable.
      const joiner = prev.text && block.text
        ? (prev.text.endsWith('\n') || block.text.startsWith('\n') ? '' : '\n')
        : '';
      next[next.length - 1] = {
        ...prev,
        text: `${prev.text}${joiner}${block.text}`,
      };
      return;
    }
    next.push(block);
  });
  if (!next.length) return [createEmptyTextBlock()];
  if (next[next.length - 1].kind === 'log') {
    next.push(createEmptyTextBlock());
  }
  return next;
};

export type DescriptionBlockPayload =
  | { kind: 'text', text: string }
  | {
    kind: 'log',
    sourceId?: string,
    tmpId?: number,
    message: string,
    type?: string,
    date?: string,
    value?: string,
    units?: string,
    valueMode?: 'value' | 'units',
    /** Frozen ids when this chip was a Combine SKU row. */
    combinedSourceIds?: string[],
    combinedDetails?: string,
    auditType?: string,
  };

export const toDescriptionBlockPayload = (
  blocks: ReportDescriptionBlock[],
): DescriptionBlockPayload[] => (
  blocks
    .map((b) => {
      if (b.kind === 'text') {
        return { kind: 'text' as const, text: b.text };
      }
      return {
        kind: 'log' as const,
        sourceId: b.sourceId,
        tmpId: b.tmpId,
        message: b.message,
        type: b.type,
        date: b.date,
        value: b.value,
        units: b.units,
        ...(b.valueMode === 'units' ? { valueMode: 'units' as const } : {}),
        ...(b.combinedSourceIds?.length
          ? { combinedSourceIds: [...b.combinedSourceIds] }
          : {}),
        ...(b.combinedDetails ? { combinedDetails: b.combinedDetails } : {}),
        ...(b.auditType ? { auditType: b.auditType } : {}),
      };
    })
    // Drop trailing empty text for the wire payload.
    .filter((b, i, arr) => !(
      b.kind === 'text'
      && !String(b.text || '').trim()
      && i === arr.length - 1
      && arr.length > 1
    ))
);

/** Rebuild editor blocks from a stored report payload (mirror / copy to new). */
export const descriptionBlocksFromPayload = (
  raw: unknown,
): ReportDescriptionBlock[] => {
  if (!Array.isArray(raw) || !raw.length) return createInitialDescriptionBlocks();
  const out: ReportDescriptionBlock[] = [];
  raw.forEach((item, i) => {
    if (!item || typeof item !== 'object') return;
    const row = item as DescriptionBlockPayload;
    if (row.kind === 'log') {
      out.push({
        id: newReportBlockId(),
        kind: 'log',
        sourceId: String(row.sourceId || ''),
        tmpId: Number(row.tmpId) > 0 ? Math.floor(Number(row.tmpId)) : i + 1,
        message: String(row.message || ''),
        type: String(row.type || 'Audit'),
        date: row.date ? String(row.date) : undefined,
        value: row.value != null ? String(row.value) : undefined,
        units: row.units != null ? String(row.units) : undefined,
        valueMode: row.valueMode === 'units' ? 'units' : undefined,
        combinedSourceIds: Array.isArray(row.combinedSourceIds)
          ? row.combinedSourceIds.map((id) => String(id || '').trim()).filter(Boolean)
          : undefined,
        combinedDetails: row.combinedDetails ? String(row.combinedDetails) : undefined,
        auditType: row.auditType ? String(row.auditType) : undefined,
      });
      return;
    }
    if (row.kind === 'text') {
      out.push({
        id: newReportBlockId(),
        kind: 'text',
        text: String(row.text || ''),
      });
    }
  });
  return normalizeDescriptionBlocks(out);
};
